import React from "react";
import { createLogger } from "../utils/logger";

const logger = createLogger("ErrorBoundary");

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      timestamp: null,
      expanded: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, timestamp: new Date().toISOString() };
  }

  componentDidCatch(error, errorInfo) {
    // Increment error count
    const errorCount = this.state.errorCount + 1;

    // Safely get current URL
    let currentUrl = "Unknown";
    try {
      if (typeof window !== "undefined" && window.location) {
        currentUrl = window.location.href;
      }
    } catch (e) {
      currentUrl = "Unable to access window.location";
    }

    // Log error details with full context
    logger.error("💥 React Error Boundary Caught");
    logger.error("Error:", error);
    logger.error("Error Info:", errorInfo);
    logger.error("Component Stack:", errorInfo.componentStack);
    logger.error("Error Message:", error.message);
    logger.error("Error Stack:", error.stack);
    logger.error("Timestamp:", new Date().toISOString());
    logger.error("Error Count:", errorCount);
    logger.error("User Agent:", navigator.userAgent);
    logger.error("URL:", currentUrl);

    // Update state with error details
    this.setState({
      hasError: true,
      error: error,
      errorInfo: errorInfo,
      errorCount: errorCount,
      timestamp: new Date().toISOString()
    });

    // Alert in dev mode for immediate attention
    if (process.env.NODE_ENV === "development") {
      logger.warn("🚨 Development Mode Alert: Error caught by boundary");
    }

    // Send error to logging service (if configured)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Placeholder for error logging service integration
    // You can integrate with services like Sentry, LogRocket, etc.
    try {
      // Safely get current URL
      let currentUrl = "Unknown";
      try {
        if (typeof window !== "undefined" && window.location) {
          currentUrl = window.location.href;
        }
      } catch (e) {
        currentUrl = "Unable to access window.location";
      }

      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: this.state.timestamp,
        userAgent: navigator.userAgent,
        url: currentUrl,
        errorCount: this.state.errorCount
      };

      // Example: Send to your backend or logging service
      // fetch('/api/log-error', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // });

      logger.log("Error data prepared for logging:", errorData);
    } catch (loggingError) {
      logger.error("Failed to log error:", loggingError);
    }
  };

  handleReload = () => {
    try {
      if (typeof window !== "undefined" && window.location) {
        window.location.reload();
      }
    } catch (e) {
      logger.error("Failed to reload:", e);
    }
  };

  handleGoHome = () => {
    try {
      if (typeof window !== "undefined" && window.location) {
        window.location.href = "/";
      }
    } catch (e) {
      logger.error("Failed to navigate home:", e);
    }
  };

  toggleExpanded = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  copyErrorToClipboard = () => {
    // Safely get current URL
    let currentUrl = "Unknown";
    try {
      if (typeof window !== "undefined" && window.location) {
        currentUrl = window.location.href;
      }
    } catch (e) {
      currentUrl = "Unable to access window.location";
    }

    const errorText = `
Error: ${this.state.error?.message || "Unknown error"}
Timestamp: ${this.state.timestamp}
URL: ${currentUrl}
User Agent: ${navigator.userAgent}

Stack Trace:
${this.state.error?.stack || "No stack trace available"}

Component Stack:
${this.state.errorInfo?.componentStack || "No component stack available"}
    `.trim();

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(errorText)
        .then(() => alert("Error details copied to clipboard!"))
        .catch(() => alert("Failed to copy to clipboard"));
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = errorText;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        alert("Error details copied to clipboard!");
      } catch (err) {
        alert("Failed to copy to clipboard");
      }
      document.body.removeChild(textArea);
    }
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === "development";

      return (
        <div
          style={{
            padding: "20px",
            backgroundColor: "#fff3cd",
            border: "2px solid #ffc107",
            borderRadius: "12px",
            margin: "20px",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            maxWidth: "800px",
            marginLeft: "auto",
            marginRight: "auto"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "15px"
            }}
          >
            <span style={{ fontSize: "48px", marginRight: "15px" }}>⚠️</span>
            <div>
              <h1
                style={{
                  color: "#ff114d",
                  margin: "0 0 5px 0",
                  fontSize: "24px"
                }}
              >
                Something went wrong
              </h1>
              <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                The application encountered an unexpected error
              </p>
            </div>
          </div>

          {/* Error summary */}
          <div
            style={{
              backgroundColor: "#fff",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "15px",
              border: "1px solid #ddd"
            }}
          >
            <p style={{ margin: "0 0 10px 0", fontWeight: "bold" }}>
              Error Message:
            </p>
            <p
              style={{
                margin: 0,
                color: "#d32f2f",
                fontFamily: "monospace",
                fontSize: "14px",
                wordBreak: "break-word"
              }}
            >
              {this.state.error?.message || "Unknown error"}
            </p>
          </div>

          {/* Stack Trace - Prominent in development with source maps */}
          {isDevelopment && process.env.GENERATE_SOURCEMAP === "true" && (
            <div
              style={{
                backgroundColor: "#fff",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "15px",
                border: "2px solid #2196F3"
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px"
                }}
              >
                <span style={{ fontSize: "20px", marginRight: "8px" }}>🗺️</span>
                <p style={{ margin: 0, fontWeight: "bold", color: "#1976d2" }}>
                  Source-Mapped Stack Trace (Click lines to debug in Safari
                  Inspector)
                </p>
              </div>
              <pre
                style={{
                  fontSize: "12px",
                  overflow: "auto",
                  backgroundColor: "#f5f5f5",
                  padding: "15px",
                  borderRadius: "4px",
                  maxHeight: "300px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                  border: "1px solid #ddd",
                  fontFamily:
                    "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
                  lineHeight: "1.5"
                }}
              >
                {this.state.error?.stack || "No stack trace available"}
              </pre>
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#1565c0"
                }}
              >
                💡 <strong>Tip:</strong> Open Safari Web Inspector (Develop →
                Device → localhost) to click file links and jump to source code
              </div>
            </div>
          )}

          {/* Metadata */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "10px",
              borderRadius: "8px",
              marginBottom: "15px",
              fontSize: "12px",
              color: "#666"
            }}
          >
            <div>
              <strong>Timestamp:</strong> {this.state.timestamp}
            </div>
            <div>
              <strong>Error Count:</strong> {this.state.errorCount}
            </div>
            <div>
              <strong>Environment:</strong>{" "}
              {process.env.NODE_ENV || "production"}
            </div>
          </div>

          {/* Expandable details */}
          <details
            open={this.state.expanded}
            style={{
              marginBottom: "15px",
              backgroundColor: "#fff",
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #ddd"
            }}
          >
            <summary
              onClick={this.toggleExpanded}
              style={{
                cursor: "pointer",
                fontWeight: "bold",
                marginBottom: "10px",
                userSelect: "none",
                color: "#333"
              }}
            >
              🔍 Click for detailed error information
            </summary>

            {/* Error Stack */}
            <div style={{ marginTop: "15px" }}>
              <p style={{ fontWeight: "bold", marginBottom: "5px" }}>
                Error Stack:
              </p>
              <pre
                style={{
                  fontSize: "11px",
                  overflow: "auto",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word"
                }}
              >
                {this.state.error?.stack || "No stack trace available"}
              </pre>
            </div>

            {/* Component Stack */}
            <div style={{ marginTop: "15px" }}>
              <p style={{ fontWeight: "bold", marginBottom: "5px" }}>
                Component Stack:
              </p>
              <pre
                style={{
                  fontSize: "11px",
                  overflow: "auto",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word"
                }}
              >
                {this.state.errorInfo?.componentStack ||
                  "No component stack available"}
              </pre>
            </div>

            {/* Browser Info */}
            {isDevelopment && (
              <div style={{ marginTop: "15px" }}>
                <p style={{ fontWeight: "bold", marginBottom: "5px" }}>
                  Browser Info:
                </p>
                <div
                  style={{
                    fontSize: "11px",
                    backgroundColor: "#f5f5f5",
                    padding: "10px",
                    borderRadius: "4px"
                  }}
                >
                  <div>
                    <strong>URL:</strong>{" "}
                    {(() => {
                      try {
                        return typeof window !== "undefined" && window.location
                          ? window.location.href
                          : "Unknown";
                      } catch (e) {
                        return "Unable to access";
                      }
                    })()}
                  </div>
                  <div>
                    <strong>User Agent:</strong> {navigator.userAgent}
                  </div>
                  <div>
                    <strong>Screen:</strong>{" "}
                    {window.screen
                      ? `${window.screen.width} x ${window.screen.height}`
                      : "Unknown"}
                  </div>
                  <div>
                    <strong>Viewport:</strong>{" "}
                    {typeof window !== "undefined"
                      ? `${window.innerWidth} x ${window.innerHeight}`
                      : "Unknown"}
                  </div>
                </div>
              </div>
            )}
          </details>

          {/* Action buttons */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap"
            }}
          >
            <button
              onClick={this.handleReload}
              style={{
                padding: "12px 24px",
                backgroundColor: "#ff114d",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                flex: "1",
                minWidth: "120px"
              }}
            >
              🔄 Reload App
            </button>

            <button
              onClick={this.handleGoHome}
              style={{
                padding: "12px 24px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                flex: "1",
                minWidth: "120px"
              }}
            >
              🏠 Go Home
            </button>

            <button
              onClick={this.copyErrorToClipboard}
              style={{
                padding: "12px 24px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                flex: "1",
                minWidth: "120px"
              }}
            >
              📋 Copy Error
            </button>
          </div>

          {/* Development mode indicator */}
          {isDevelopment && (
            <div
              style={{
                marginTop: "15px",
                padding: "10px",
                backgroundColor: "#e3f2fd",
                borderRadius: "8px",
                fontSize: "12px",
                color: "#1976d2",
                textAlign: "center"
              }}
            >
              🔧 Running in Development Mode - Additional debugging info
              available in console
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
